/**
 * The study room's plan (2026-10-05, reshaped 2026-10-07): the hall's outline, the table, and
 * where the dashboard arc, the centre screen, the studio bay and the ward's arch stand. Pure
 * numbers, no three.js, so the room, the tests and the 2D /queries map share one source.
 *
 * The centre screen faces the table from the middle of the arc; the eleven dashboards stand
 * either side of it at their usual size, six on the left and five on the right (the right
 * end opens onto the ward). Fitting them all on one arc with the screen between them needs a
 * 10.3 m radius.
 *
 * Since 2026-10-07 (the user's request) the north wall is one circle round the table with the
 * dashboards set into it, no gap behind them; the hall runs 16 m in front of the table so the
 * ward shows from the door; a studio bay opens off the west wall (whiteboard, tray, model
 * shelf); the presentation display hangs on the south wall; and the ward stands half in the
 * hall and half in a room of its own through an arch in the east wall (`ward/layout.ts`
 * sizes that room from the study's subject count).
 */

export interface Point {
  x: number;
  z: number;
}

/**
 * The centre of the curve: the dashboards, the north wall and the data horizon stand on
 * circles round it. Until 2026-10-07 the table stood here too.
 */
export const ARC_CENTRE = { x: 0, z: 0.5 };

/**
 * The table, its chairs, console and the rings over it: 2 m nearer the centre screen than the
 * curve's centre (2026-10-07, the user's choice after renders at 2.5 and 3 m), 7.3 m from it.
 */
export const TABLE_SHIFT = 2;
export const TABLE = { x: ARC_CENTRE.x, z: ARC_CENTRE.z - TABLE_SHIFT };

export const ARC = {
  radius: 10.3,
  panelWidth: 2.4,
  panelHeight: 1.53,
  panelCentreY: 1.85,
  /** Between neighbouring panels, and either side of the centre screen. */
  gap: 0.2,
  /** Panels left of the centre screen (the rest stand on its right). */
  leftCount: 6,
};

/**
 * The centre screen: a dashboard panel scaled up, centred on the arc's middle (270 degrees).
 * Since 2026-10-07 (the user's request) it is 9 m wide: 1.5 times a scaled panel's width at
 * the same 3.8 m height. Its canvases are drawn that much wider (`wide`), so the summary and a
 * projected dashboard use the whole width rather than stretching.
 */
export const CENTRE = {
  angle: (3 * Math.PI) / 2,
  scale: 2.5,
  wide: 1.5,
  /**
   * Raised so the projected dashboard's 3D chart can stand on the floor in front of it
   * without hiding its lower edge from the table (2026-10-05): bottom 1.2 m, top 5.0 m.
   */
  centreY: 3.12,
  get width(): number {
    return ARC.panelWidth * this.scale * this.wide;
  },
  get height(): number {
    return ARC.panelHeight * this.scale;
  },
  /** How far inside the arc the flat screen stands: just in front of the wall's flat section. */
  get inset(): number {
    return ARC.radius - centreWall().distance + 0.06;
  },
};

/**
 * The projected dashboard's floor stage, larger, on the floor in front of the centre screen.
 * Objects stay under 0.75 m x scale, below the screen's lower edge as seen from the table.
 */
export const CENTRE_STAGE = { scale: 1.6 };

/**
 * The same stage in miniature over the table, on a lit glass plinth just beyond the room
 * console, high enough to show over the console's raised far edge. `origin` is the stage's
 * back edge; it faces the entrance, where the console's user stands.
 */
export const TABLE_STAGE = {
  scale: 0.22,
  y: 1.3,
  /** Relative to the table's centre (it was at z 0.27 and 0.46 with the table at 0.5). */
  get origin(): { x: number; z: number } {
    return { x: TABLE.x, z: TABLE.z - 0.23 };
  },
  get plinth(): { x: number; z: number; radius: number } {
    return { x: TABLE.x, z: TABLE.z - 0.04, radius: 0.32 };
  },
};

export interface ArcPlan {
  /** Centre angle of each dashboard panel in arc order (x = cos a, z = sin a around the table). */
  angles: number[];
  /** Angle between neighbouring panel centres. */
  pitch: number;
  /** Half the angle the centre screen and its gaps take. */
  centreHalf: number;
  /** The arc's outer edges. */
  start: number;
  end: number;
}

export function arcPlan(count = 11): ArcPlan {
  const pitch = (ARC.panelWidth + ARC.gap) / ARC.radius;
  // The dashboards start just past the flat section of wall the centre screen is set into.
  const centreHalf = centreWall().half + ARC.gap / ARC.radius;
  const left = Math.min(ARC.leftCount, count);
  const angles = Array.from({ length: count }, (_, i) =>
    i < left
      ? CENTRE.angle - centreHalf - pitch * (left - i - 0.5)
      : CENTRE.angle + centreHalf + pitch * (i - left + 0.5),
  );
  return {
    angles,
    pitch,
    centreHalf,
    start: angles[0]! - pitch / 2,
    end: angles[count - 1]! + pitch / 2,
  };
}

/** A point on a circle around the table at `angle`. */
export function aroundArc(angle: number, radius: number): { x: number; z: number } {
  return {
    x: ARC_CENTRE.x + Math.cos(angle) * radius,
    z: ARC_CENTRE.z + Math.sin(angle) * radius,
  };
}

/* ---------- The hall's outline (2026-10-07) ---------- */

/**
 * The north wall: one circle round the table, just behind the dashboards' backdrop (which
 * stands at the arc radius + 0.22), so the screens sit in the wall with no gap behind them.
 * It runs from the arc's west end to its east end, flat behind the centre screen; straight
 * walls join it to the side walls (curves until the centre screen widened, 2026-10-07).
 */
export const NORTH_WALL = {
  radius: ARC.radius + 0.24,
  /** The flat section behind the centre screen reaches this far past the screen's edges. */
  centreMargin: 0.35,
  /** How far past each end of the arc the circle runs (the end ribs stand there). */
  overrun: (1.2 * Math.PI) / 180,
};

/**
 * The hall: x from -halfWidth (the west wall; the studio bay opens beyond it) to +halfWidth
 * (the east wall with the ward's arch), z from back (the top of the north wall's circle) to
 * front (the entrance wall).
 */
export const HALL = { halfWidth: 13, front: 16, back: ARC_CENTRE.z - NORTH_WALL.radius };

/** Where people arrive, and the door's half width in the entrance wall. */
export const SPAWN = { x: 0, z: 14.6 };
export const DOOR_HALF = 1.7;
/** Radius of the two front corners. */
export const FRONT_CORNER = 1.2;

/**
 * The collaboration studio: a bay off the west wall (x0 is its back wall), open to the hall
 * between z0 and z1. The whiteboard hangs on its back wall with the tool tray beside it at the
 * entrance end, the model shelf stands against its north wall.
 */
export const STUDIO = { x0: -19, z0: 6.1, z1: 14.1 };

/**
 * The studio's whiteboard on the bay's back wall (centre z) and its tool tray beside it at the
 * entrance end; the model shelf stands against the bay's north wall (collab/ModelShelf.ts).
 */
export const STUDIO_FITTINGS = {
  boardZ: 9.8,
  trayZ: 13.45,
  /** The model shelf's centre: its back to the north wall, its front facing south (+z). */
  shelf: { x: -16, z: STUDIO.z0 + 0.45 },
};

/**
 * The ward's place in the plan: the focal ring the mannequins face stands in the hall, `arch`
 * metres in front of the east wall; the arch in that wall is as wide as the fan of rows at that
 * distance, so the ward room behind it (sized by `ward/layout.ts`) continues the fan exactly.
 */
export const WARD_SITE = {
  focal: { x: 10.2, z: 9.5 },
  /** 2.8 m: with today's 33 to 50 subjects, 40 to 55 percent stand in the hall. */
  arch: 2.8,
  halfAngle: (55 * Math.PI) / 180,
};

/** The arch in the east wall that opens into the ward room. */
export function wardArch(): { x: number; z0: number; z1: number } {
  const half = WARD_SITE.arch * Math.tan(WARD_SITE.halfAngle);
  return {
    x: WARD_SITE.focal.x + WARD_SITE.arch,
    z0: WARD_SITE.focal.z - half,
    z1: WARD_SITE.focal.z + half,
  };
}

/**
 * The flat section of the north wall the centre screen is set into (2026-10-07: a 9 m flat
 * screen would stand a metre proud of the curve at its middle): a chord of the wall's circle,
 * `half` its half-angle from the centre, `distance` its distance from the table.
 */
export function centreWall(): { half: number; distance: number } {
  const half = Math.asin((CENTRE.width / 2 + NORTH_WALL.centreMargin) / NORTH_WALL.radius);
  return { half, distance: NORTH_WALL.radius * Math.cos(half) };
}

/** The north wall's two ends (angles in the arc's convention) and their points. */
export function northWallEnds(): {
  west: number;
  east: number;
  westPoint: Point;
  eastPoint: Point;
} {
  const plan = arcPlan();
  const west = plan.start - NORTH_WALL.overrun;
  const east = plan.end + NORTH_WALL.overrun;
  return {
    west,
    east,
    westPoint: aroundArc(west, NORTH_WALL.radius),
    eastPoint: aroundArc(east, NORTH_WALL.radius),
  };
}

function arcPoints(cx: number, cz: number, r: number, from: number, to: number, step = 0.35) {
  const n = Math.max(4, Math.ceil((Math.abs(to - from) * r) / step));
  const out: Point[] = [];
  for (let i = 1; i <= n; i++) {
    const a = from + ((to - from) * i) / n;
    out.push({ x: cx + Math.cos(a) * r, z: cz + Math.sin(a) * r });
  }
  return out;
}

/**
 * The hall's walls in plan, from the entrance's west end round the studio bay, the north wall
 * and down the east wall to the entrance's east end. The entrance itself (z = front) is glass,
 * built separately. The east wall passes straight across the ward's arch here: the floor and
 * ceiling stop at that line, and `hallWallRuns` leaves the arch open.
 */
export function hallOutline(): Point[] {
  const hx = HALL.halfWidth;
  const hz = HALL.front;
  const r = FRONT_CORNER;
  const { west, east, westPoint, eastPoint } = northWallEnds();
  const arch = wardArch();
  const pts: Point[] = [{ x: -hx + r, z: hz }];
  pts.push(...arcPoints(-hx + r, hz - r, r, Math.PI / 2, Math.PI, 0.25));
  // Up the west wall to the studio bay, round the bay, then on to the north wall.
  pts.push({ x: -hx, z: STUDIO.z1 });
  pts.push({ x: STUDIO.x0, z: STUDIO.z1 });
  pts.push({ x: STUDIO.x0, z: STUDIO.z0 });
  pts.push({ x: -hx, z: STUDIO.z0 });
  // The studio's north wall runs straight on to the dashboard wall's west end, where a rib
  // stands; the circle carries the dashboards, with a flat section behind the centre screen;
  // a straight wall joins its east end to the east wall.
  pts.push(westPoint);
  const flat = centreWall().half;
  pts.push(...arcPoints(ARC_CENTRE.x, ARC_CENTRE.z, NORTH_WALL.radius, west, CENTRE.angle - flat));
  pts.push(aroundArc(CENTRE.angle + flat, NORTH_WALL.radius));
  pts.push(...arcPoints(ARC_CENTRE.x, ARC_CENTRE.z, NORTH_WALL.radius, CENTRE.angle + flat, east));
  pts.push({ x: hx, z: eastPoint.z });
  pts.push({ x: hx, z: arch.z0 });
  pts.push({ x: hx, z: arch.z1 });
  pts.push({ x: hx, z: hz - r });
  pts.push(...arcPoints(hx - r, hz - r, r, 0, Math.PI / 2, 0.25));
  return pts;
}

/** The hall's walls as runs of points: the outline with the ward's arch left open. */
export function hallWallRuns(): Point[][] {
  const pts = hallOutline();
  const arch = wardArch();
  const i = pts.findIndex((p) => p.x === HALL.halfWidth && p.z === arch.z0);
  return [pts.slice(0, i + 1), pts.slice(i + 1)];
}

/** Whether a point lies inside a polygon (even-odd rule). */
export function insidePolygon(p: Point, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if (a.z > p.z !== b.z > p.z && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x)
      inside = !inside;
  }
  return inside;
}

/** Distance from a point to a polygon's boundary. */
export function distanceToEdges(p: Point, poly: Point[]): number {
  let best = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[j]!;
    const b = poly[i]!;
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len2 = dx * dx + dz * dz || 1;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2));
    best = Math.min(best, Math.hypot(p.x - (a.x + dx * t), p.z - (a.z + dz * t)));
  }
  return best;
}

/**
 * The reception just inside the entrance, right of the walkway, turned toward the spawn point:
 * a counter with Luma, the host, behind it (her card is the room's AI assistant). Local frame:
 * +z toward the visitor, +x to the visitor's right.
 */
export const RECEPTION = {
  x: 1.75,
  z: 12.9,
  yaw: Math.atan2(SPAWN.x - 1.75, SPAWN.z - 12.9),
  counter: { width: 1.9, depth: 0.55, height: 1.05 },
  host: { x: 0, z: -0.62 },
};

/**
 * The presentation display on the south wall, west of the door, facing north into the hall;
 * the lectern beside it at its west end, its top facing the wall so the presenter stands
 * between it and the wall, facing the audience (2026-10-07; enlarged the same day from
 * 5 x 2.8 m, the user's request).
 */
export const PRESENTATION = {
  x: -7.4,
  /** The display's width and height, and its centre's height. */
  width: 7.2,
  height: 4.0,
  y: 2.6,
  lectern: { x: -11.75, z: HALL.front - 1.15, yaw: 0 },
};

/** A point in the reception's frame, in room coordinates. */
export function receptionPoint(x: number, z: number): { x: number; z: number } {
  const c = Math.cos(RECEPTION.yaw);
  const s = Math.sin(RECEPTION.yaw);
  return { x: RECEPTION.x + x * c + z * s, z: RECEPTION.z - x * s + z * c };
}
